import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ScrollableCurriculumTabStrip } from "@/components/ui/scrollable-curriculum-tab-strip";
import { Tabs, TabsContent, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { BeltRank } from "../../MembersArea";

const GYEOMJEON_TABS = ["Hyo", "Choong", "Shin", "Eui"] as const;
const DEFAULT_TAB_SURFACE = "!bg-gray-200 !text-gray-900";

interface GyeomJeonProps {
  selectedBelt: BeltRank;
  getBeltColor: (belt: BeltRank) => string;
  hasDemo?: (aliases: string[]) => boolean;
  onWatchDemo?: (aliases: string[]) => void;
  presentation?: "legacy" | "curriculum";
  beltTabSurfaceClass?: string;
}

interface GyeomJeonPattern {
  number: number;
  name: string;
  meaning: string;
  movements: string;
  keyTechniques: string[];
}

const gyeomJeonPatterns: GyeomJeonPattern[] = [
  {
    number: 1,
    name: "GyeomJeon Hyo - Modesty Respect & Filial Duty",
    meaning: "Humble Perfection First Form",
    movements:
      "The pattern consists of 38 movements that demonstrate foundational techniques performed with grace and humility.",
    keyTechniques: [
      "Basic combination techniques",
      "Foundational transitional movements",
      "Balance exercises",
      "Basic striking methods",
      "Energy flow introduction",
      "Humble execution focus",
    ],
  },
  {
    number: 2,
    name: "GyeomJeon Choong - Modesty Respect & Loyalty",
    meaning: "Humble Perfection Second Form",
    movements:
      "The pattern consists of 46 movements that build upon the first form with more complex techniques.",
    keyTechniques: [
      "Intermediate combination techniques",
      "Advanced transitional movements",
      "Balance and control exercises",
      "Precision striking methods",
      "Energy flow development",
      "Advanced humble execution",
    ],
  },
  {
    number: 3,
    name: "GyeomJeon Shin - Modesty Respect & Faith (Trust)",
    meaning: "Humble Perfection Third Form",
    movements:
      "The pattern consists of 52 movements that demonstrate advanced techniques with increased complexity.",
    keyTechniques: [
      "Advanced combination techniques",
      "Complex transitional movements",
      "Advanced balance control",
      "Master-level striking methods",
      "Energy flow mastery",
      "Expert humble execution",
    ],
  },
  {
    number: 4,
    name: "GyeomJeon Eui - Modesty Respect & Righteousness",
    meaning: "Humble Perfection Fourth Form",
    movements:
      "The pattern consists of 58 movements that represent the pinnacle of the GyeomJeon series.",
    keyTechniques: [
      "Master-level combination techniques",
      "Expert transitional movements",
      "Supreme balance control",
      "Advanced precision striking",
      "Complete energy flow mastery",
      "Perfect humble execution",
    ],
  },
];

const demoAliasesByPattern: Record<number, string[]> = {
  1: ["gyeomjeon_hyo"],
  2: ["gyeomjeon_choong"],
  3: ["gyeomjeon_shin"],
  4: ["gyeomjeon_eui"],
};

function GyeomJeonPanels({
  pattern,
  hasDemo,
  onWatchDemo,
  showTitle,
}: {
  pattern: GyeomJeonPattern;
  hasDemo?: (aliases: string[]) => boolean;
  onWatchDemo?: (aliases: string[]) => void;
  showTitle?: boolean;
}) {
  return (
    <div className="space-y-4">
      {showTitle && (
        <h2 className="text-xl font-semibold text-taekwondo-red">{pattern.name}</h2>
      )}
      {demoAliasesByPattern[pattern.number] && hasDemo?.(demoAliasesByPattern[pattern.number]) && (
        <div>
          <button
            type="button"
            className="inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
            onClick={() => onWatchDemo?.(demoAliasesByPattern[pattern.number])}
          >
            Watch Demonstration Video
          </button>
        </div>
      )}
      <div>
        <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Information</h4>
        <p className="mb-4 text-gray-700">
          {pattern.name} is a master-level pattern that represents the culmination of modesty and humility in
          martial arts practice.
        </p>
      </div>
      <div>
        <h4 className="mb-3 text-base font-medium text-taekwondo-red">Meaning</h4>
        <p className="text-gray-700">{pattern.meaning}</p>
      </div>
      <div>
        <h4 className="mb-3 text-base font-medium text-taekwondo-red">Movements</h4>
        <p className="text-gray-700">{pattern.movements}</p>
      </div>
      <div>
        <h4 className="mb-3 text-base font-medium text-taekwondo-red">Key Techniques</h4>
        <ul className="list-inside list-disc space-y-1 text-gray-700">
          {pattern.keyTechniques.map((technique, index) => (
            <li key={index}>{technique}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function GyeomJeon({
  selectedBelt,
  getBeltColor: _getBeltColor,
  hasDemo,
  onWatchDemo,
  presentation = "legacy",
  beltTabSurfaceClass = DEFAULT_TAB_SURFACE,
}: GyeomJeonProps) {
  const getAvailablePatterns = () => {
    switch (selectedBelt) {
      case "NAVY":
        return gyeomJeonPatterns.slice(0, 1);
      case "BLACK/WHITE":
        return gyeomJeonPatterns.slice(0, 1);
      case "BLACK/D1":
        return gyeomJeonPatterns.slice(0, 2);
      case "BLACK/D2":
        return gyeomJeonPatterns.slice(0, 3);
      case "BLACK/D3":
      case "BLACK/D4":
      case "BLACK/D5":
        return gyeomJeonPatterns.slice(0, 4);
      default:
        return [];
    }
  };

  const availablePatterns = getAvailablePatterns();
  if (availablePatterns.length === 0) return null;

  const fingerprint = availablePatterns.map((p) => p.number).join("|");
  const [activeTab, setActiveTab] = useState(() => String(availablePatterns[0]?.number ?? 1));

  useEffect(() => {
    setActiveTab(String(availablePatterns[0]?.number ?? 1));
  }, [fingerprint]);

  const surface = beltTabSurfaceClass;

  if (presentation === "curriculum") {
    return (
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-4">
        <ScrollableCurriculumTabStrip key={fingerprint} ariaLabelPrefix="GyeomJeon forms">
          {availablePatterns.map((p) => (
            <TabsTrigger
              key={p.number}
              value={String(p.number)}
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-4 py-2 text-sm font-medium transition-shadow",
                surface,
                "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
              )}
            >
              {GYEOMJEON_TABS[p.number - 1] ?? p.name}
            </TabsTrigger>
          ))}
        </ScrollableCurriculumTabStrip>

        {availablePatterns.map((p) => (
          <TabsContent
            key={p.number}
            value={String(p.number)}
            className="mt-0 focus-visible:outline-none"
          >
            <div className="mt-2 rounded-lg bg-gray-50 p-4 md:p-5">
              <GyeomJeonPanels pattern={p} hasDemo={hasDemo} onWatchDemo={onWatchDemo} showTitle />
            </div>
          </TabsContent>
        ))}
      </Tabs>
    );
  }

  return (
    <Accordion type="single" collapsible className="w-full space-y-2">
      {availablePatterns.map((pattern) => (
        <AccordionItem key={`gyeomjeon-${pattern.number}`} value={`gyeomjeon-${pattern.number}`}>
          <AccordionTrigger className="rounded-lg bg-gray-50 px-4 py-2 hover:bg-gray-100 hover:no-underline">
            <div className="flex w-full items-center text-left">
              <span className="flex-1">{pattern.name}</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-0">
            <div className="mt-2 rounded-lg bg-gray-50 p-4">
              <GyeomJeonPanels pattern={pattern} hasDemo={hasDemo} onWatchDemo={onWatchDemo} />
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
